import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backend';
import { usePageMeta } from '../hooks/usePageMeta';
import { Lock, Download, RefreshCw, LogOut, Users, Music2, Check, Trash2 } from 'lucide-react';

const INTEREST_LABELS = {
  group_4_6: 'Gruppo 4-6 anni',
  individual_6_plus: 'Individuale dai 6 anni',
  open_day: 'Open Day',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const MesAdmin = () => {
  usePageMeta({ title: 'MES — Gestione contatti', canonicalPath: '/mes-admin', noindex: true });

  const [password, setPassword] = useState(sessionStorage.getItem('mes_admin_pw') || '');
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);

  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === leads.length ? [] : leads.map((l) => l.id)));

  const deleteSelected = async () => {
    const pw = sessionStorage.getItem('mes_admin_pw');
    if (!pw || selected.length === 0) return;
    if (!window.confirm(`Eliminare ${selected.length} contatto/i? L'operazione non è reversibile.`)) return;
    const ids = [...selected];
    try {
      await axios.post(`${BACKEND_URL}/api/mes/admin/leads/delete`, { password: pw, ids });
      setLeads((prev) => prev.filter((l) => !ids.includes(l.id)));
      setSelected([]);
    } catch {
      window.alert('Errore durante l’eliminazione. Riprova.');
    }
  };

  const fetchLeads = useCallback(async (pw) => {
    setStatus('loading');
    setError('');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/mes/admin/leads`, { password: pw });
      setLeads(data.leads || []);
      setAuthed(true);
      sessionStorage.setItem('mes_admin_pw', pw);
      setStatus('idle');
    } catch (e) {
      setAuthed(false);
      setStatus('error');
      setError(e.response?.status === 401 ? 'Password non valida.' : 'Errore di connessione. Riprova.');
      if (e.response?.status === 401) sessionStorage.removeItem('mes_admin_pw');
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem('mes_admin_pw');
    if (saved) fetchLeads(saved);
  }, [fetchLeads]);

  const submit = (e) => {
    e.preventDefault();
    if (password.trim()) fetchLeads(password.trim());
  };

  const logout = () => {
    sessionStorage.removeItem('mes_admin_pw');
    setAuthed(false);
    setPassword('');
    setLeads([]);
    setSelected([]);
  };

  const markRead = async (id) => {
    const pw = sessionStorage.getItem('mes_admin_pw');
    if (!pw || !id) return;
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: 'read' } : l)));
    try {
      await axios.post(`${BACKEND_URL}/api/mes/admin/leads/${id}/read`, { password: pw });
    } catch {
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: 'new' } : l)));
    }
  };

  const newCount = leads.filter((l) => (l.status || 'new') === 'new').length;

  const downloadCsv = () => {
    const headers = ['Data', 'Genitore', 'Bambino', 'Età', 'Telefono', 'Email', 'Interesse', 'Messaggio'];
    const rows = leads.map((l) => [
      fmtDate(l.created_at), l.parent_name || '', l.child_name || '', l.child_age || '',
      l.phone || '', l.email || '', INTEREST_LABELS[l.interest] || l.interest || '', (l.message || '').replace(/\n/g, ' '),
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mes-contatti-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#14213D] px-5" data-testid="mes-admin-login">
        <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-[#F7F1E6] p-8 shadow-2xl">
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#14746F]/10 text-[#14746F]"><Lock size={22} /></div>
            <h1 className="font-serif text-2xl font-bold text-[#14213D]" style={{ fontFamily: 'Georgia, serif' }}>MES — Gestione contatti</h1>
            <p className="mt-1 text-sm text-[#14213D]/60">Inserisci la password per accedere ai lead.</p>
          </div>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" autoFocus data-testid="mes-admin-password"
            className="w-full rounded-xl border border-[#14213D]/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#14746F]"
          />
          {error && <p className="mt-3 text-sm font-medium text-red-600" data-testid="mes-admin-error">{error}</p>}
          <button type="submit" disabled={status === 'loading'} data-testid="mes-admin-submit"
            className="mt-4 w-full rounded-full bg-[#C9A24B] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[#14213D] transition-all duration-300 hover:bg-[#14213D] hover:text-[#E9D9AE] disabled:opacity-60">
            {status === 'loading' ? 'Verifica…' : 'Accedi'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F1E6] px-5 py-10 text-[#14213D]" data-testid="mes-admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#14746F]">MES · Gestione contatti</p>
            <h1 className="mt-1 flex items-center gap-2 font-serif text-3xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>
              <Music2 size={26} className="text-[#C9A24B]" /> Lead ricevuti
            </h1>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-[#14213D]/60"><Users size={15} /> {leads.length} contatti totali{newCount > 0 && <span className="ml-1 rounded-full bg-[#C9A24B] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-[#14213D]" data-testid="mes-admin-newcount">{newCount} nuovi</span>}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {selected.length > 0 && (
              <button onClick={deleteSelected} data-testid="mes-admin-delete-selected"
                className="inline-flex items-center gap-2 rounded-full bg-red-500 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-red-600">
                <Trash2 size={15} /> Elimina selezionati ({selected.length})
              </button>
            )}
            <button onClick={() => fetchLeads(sessionStorage.getItem('mes_admin_pw'))} data-testid="mes-admin-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-[#14213D]/15 bg-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors hover:border-[#14746F] hover:text-[#14746F]">
              <RefreshCw size={15} /> Aggiorna
            </button>
            <button onClick={downloadCsv} disabled={!leads.length} data-testid="mes-admin-download"
              className="inline-flex items-center gap-2 rounded-full bg-[#14746F] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#0E4C4C] disabled:opacity-50">
              <Download size={15} /> Scarica CSV
            </button>
            <button onClick={logout} data-testid="mes-admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-[#14213D]/15 bg-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors hover:border-red-400 hover:text-red-500">
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>

        {leads.length === 0 ? (
          <div className="rounded-3xl bg-white p-12 text-center text-[#14213D]/60 ring-1 ring-[#14213D]/5" data-testid="mes-admin-empty">
            Nessun contatto ancora. I nuovi lead compariranno qui.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-3xl bg-white shadow-[0_20px_60px_-30px_rgba(20,33,61,0.35)] ring-1 ring-[#14213D]/5">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead>
                <tr className="border-b border-[#14213D]/10 bg-[#14213D]/[0.03] text-[11px] uppercase tracking-wider text-[#14213D]/60">
                  <th className="px-4 py-3">
                    <input type="checkbox" data-testid="mes-admin-selectall" aria-label="Seleziona tutti"
                      checked={leads.length > 0 && selected.length === leads.length}
                      onChange={toggleAll} className="h-4 w-4 cursor-pointer accent-[#14746F]" />
                  </th>
                  <th className="px-4 py-3 font-semibold">Data</th>
                  <th className="px-4 py-3 font-semibold">Genitore</th>
                  <th className="px-4 py-3 font-semibold">Bambino</th>
                  <th className="px-4 py-3 font-semibold">Età</th>
                  <th className="px-4 py-3 font-semibold">Telefono</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Interesse</th>
                  <th className="px-4 py-3 font-semibold">Messaggio</th>
                  <th className="px-4 py-3 font-semibold">Stato</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l, i) => {
                  const isNew = (l.status || 'new') === 'new';
                  return (
                  <tr key={l.id || i} className={`border-b border-[#14213D]/5 last:border-0 transition-colors hover:bg-[#14746F]/[0.03] ${isNew ? 'bg-[#C9A24B]/[0.06]' : ''}`} data-testid={`mes-admin-row-${i}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" data-testid={`mes-admin-select-${i}`} aria-label="Seleziona contatto"
                        checked={selected.includes(l.id)} onChange={() => toggleOne(l.id)}
                        className="h-4 w-4 cursor-pointer accent-[#14746F]" />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-[#14213D]/70">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {isNew && <span className="h-2 w-2 shrink-0 rounded-full bg-[#C9A24B]" title="Nuovo" />}
                        {l.parent_name || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{l.child_name || '—'}</td>
                    <td className="px-4 py-3">{l.child_age || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3">{l.phone ? <a href={`tel:${l.phone}`} className="text-[#14746F] hover:underline">{l.phone}</a> : '—'}</td>
                    <td className="px-4 py-3">{l.email ? <a href={`mailto:${l.email}`} className="text-[#14746F] hover:underline">{l.email}</a> : '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span className="rounded-full bg-[#C9A24B]/15 px-2.5 py-1 text-xs font-medium text-[#8a6d24]">{INTEREST_LABELS[l.interest] || l.interest || '—'}</span>
                    </td>
                    <td className="max-w-xs px-4 py-3 text-[#14213D]/70">{l.message || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {isNew ? (
                        <button onClick={() => markRead(l.id)} data-testid={`mes-admin-markread-${i}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#C9A24B] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#14213D] transition-colors hover:bg-[#14213D] hover:text-[#E9D9AE]">
                          Nuovo · segna letto
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#14213D]/40"><Check size={13} /> Letto</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MesAdmin;
